/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Position3D } from '@deck.gl/core';
import { IconLayer } from '@deck.gl/layers';
import { Dispatch } from 'redux';
import destination from '@turf/destination';
import { Position, VarName } from '../../../services/api';
import { isDataExpired } from '../../../utils/functions';
import { Marker, ICON_ATLAS, ICON_MAPPING, MarkerData } from '../mapHelpers';
import { setHighlightedItem } from '../../../state/actions';
import { ActionTypes } from '../../../state/actionTypes';

const getOccSignaturesLayer = (
  markerData: MarkerData[],
  dispatch: Dispatch<ActionTypes>
): IconLayer<Marker> | null => {
  if (!markerData?.length) {
    return null;
  }

  const createCircularPoints = (lat: number, lng: number, numPoints: number, radius: number) => {
    const center = [lng, lat];
    const points: Position[] = [];
    // Calculate angle between each point (in degrees)
    const angleStep = 360 / numPoints;

    for (let i = 0; i < numPoints; i++) {
      const bearing = i * angleStep;

      // Create point at given distance and bearing from center
      const destn = destination(center, radius, bearing, { units: 'meters' });
      const coords = destn.geometry.coordinates;

      points.push({
        lat: coords[1],
        lng: coords[0],
      });
    }

    return points;
  };

  const personMarkers: Marker[] = [];
  markerData.forEach((sensor) => {
    const { id, detail, time, value, position } = sensor;
    if (!isDataExpired(time) && detail?.positions) {
      detail.positions.forEach((point) => {
        personMarkers.push({
          id,
          location: 'location',
          name: 'name',
          coordinates: [point.lng, point.lat, point.height ?? 1.0],
          availableData: [],
        });
      });
    }

    // display relative circular pins if detail doesnt have enough positions matching to value
    if (value > 0 && detail?.positions.length !== value) {
      const targetLat = position?.lat;
      const targetLng = position?.lng;
      const requiredPins = value - (detail?.positions?.length ?? 0);
      const points =
        targetLat && targetLng
          ? createCircularPoints(targetLat, targetLng, requiredPins, 0.75)
          : [];

      points.forEach((point) => {
        personMarkers.push({
          id,
          location: 'location',
          name: 'name',
          coordinates: [point.lng, point.lat, point.height ?? 1.0],
          availableData: [],
        });
      });
    }
  });

  if (personMarkers.length === 0) {
    return null;
  }

  return new IconLayer({
    id: 'occSignatures-layer',
    data: personMarkers,
    pickable: true,
    iconAtlas: ICON_ATLAS,
    iconMapping: ICON_MAPPING,
    getIcon: () => 'personCircle',
    sizeScale: 0.5,
    sizeUnits: 'meters',
    getPosition: (d) => d.coordinates as Position3D,
    getIconColor: (d: Marker) => d?.color ?? [52, 95, 145, 128],
    onHover: (d) => {
      if (d.picked) {
        dispatch(setHighlightedItem({ id: d.object.id, varName: VarName.OccSignatures }));
      } else dispatch(setHighlightedItem({ id: '' }));
    },
  });
};

export default getOccSignaturesLayer;
